import * as React from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {addStyle, type StyleType} from "@khanacademy/wonder-blocks-core";
import {staticUrl} from "@repo/urls";

type StoreBadgeProps = {
    safeAppDownloadUrl: string;
    badgeImageUrl: string;
    altText: string;
    /**
     * Set to true if the badge will be displayed on a dark background.
     * Adjusts features like the color of the focus ring.
     */
    light?: boolean;
    style?: StyleType;
};

const StyledImg = addStyle("img");

/**
 * A container to display the badge of one of the major app stores
 * (e.g. Apple’s App Store, Google’s Play store).
 *
 * Note there may be additional requirements to meet in order to comply
 * with app store terms. For example:
 *
 * - Apple and Google require clear space around the displayed badge
 *   greater than or equal to 25% of the rendered badge height.
 * - Apple requires legal attribution (e.g. “Apple and App Store are
 *   registered trademarks of Apple Inc.”) for the use of their
 *   trademarks.
 *
 * For flexibility in layout these requirements are left to the caller
 * to implement.
 *
 * @see {@link AppStoreBadge} for a drop-in implementation of Apple’s
 *      App Store badge.
 * @see {@link GooglePlayBadge} for a drop-in implementation of
 *      Google’s Play badge.
 * @see {@link https://developer.apple.com/app-store/marketing/guidelines/|App Store Marketing Guidelines}
 * @see {@link https://play.google.com/intl/en_us/badges/|Google Play Badge Guidelines}
 */
export default function StoreBadge(props: StoreBadgeProps): React.ReactElement {
    return (
        <Clickable href={props.safeAppDownloadUrl} light={props.light}>
            {() => (
                <StyledImg
                    src={staticUrl(props.badgeImageUrl)}
                    alt={props.altText}
                    style={props.style}
                />
            )}
        </Clickable>
    );
}
